import React, { useState, useEffect } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { allAPiServicesCall } from '../../../services/apiServices';
import CommonLayout from '../../../layouts/CommonLayout';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAuthConfig } from '../../../services/apiUtils';
import { extractObjectId } from '../../../utils/utils';
import {
  FaPodcast,
  FaPlus,
  FaSearch,
  FaEye,
  FaEdit,
  FaTrash,
  FaChevronLeft,
  FaChevronRight,
  FaMicrophone,
  FaExclamationTriangle,
  FaTimes
} from 'react-icons/fa';
import './PodcastSection.css';

const PodcastSection = () => {
  const [showAddPodcastModal, setShowAddPodcastModal] = useState(false);
  const [showEditPodcastModel, setShowEditPodcastModel] = useState(false);
  const [podcastToEdit, setPodcastToEdit] = useState({});
  const [showDeletePodcastModel, setShowDeletePodcastModel] = useState(false);
  const [podcastToDelete, setPodcastToDelete] = useState("");
  const [podcasts, setPodcasts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Artist tagging state for Add modal
  const [taggedArtists, setTaggedArtists] = useState([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistResults, setArtistResults] = useState([]);
  const [showArtistDropdown, setShowArtistDropdown] = useState(false);

  // Artist tagging state for Edit modal
  const [editTaggedArtists, setEditTaggedArtists] = useState([]);
  const [editArtistSearch, setEditArtistSearch] = useState('');
  const [editArtistResults, setEditArtistResults] = useState([]);
  const [showEditArtistDropdown, setShowEditArtistDropdown] = useState(false);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  const fetchPodcasts = async (page = 1, query = '') => {
    setLoading(true);
    try {
      const response = await allAPiServicesCall.getPodcasts({ page, search: query });
      setPodcasts(response.data?.podcasts || []);
      setTotalPages(response.data?.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching podcasts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPodcasts(currentPage, searchQuery);
  }, [currentPage, searchQuery]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchPodcasts(1, searchQuery);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePodcastClick = (id) => {
    navigate(`/podcast/${id}`);
  };

  // Artist search handler for Add modal
  const handleArtistSearch = async (searchTerm) => {
    setArtistSearch(searchTerm);
    if (searchTerm.length < 2) {
      setArtistResults([]);
      setShowArtistDropdown(false);
      return;
    }

    try {
      const response = await allAPiServicesCall.getSearchArtist(
        { query: searchTerm },
        getAuthConfig(),
        navigate
      );
      if (response?.data) {
        setArtistResults(response.data);
        setShowArtistDropdown(true);
      }
    } catch (error) {
      console.error('Error searching artists:', error);
    }
  };

  const handleAddArtist = (artist) => {
    if (!taggedArtists.find(a => extractObjectId(a._id) === extractObjectId(artist._id))) {
      setTaggedArtists([...taggedArtists, artist]);
    }
    setArtistSearch('');
    setArtistResults([]);
    setShowArtistDropdown(false);
  };

  const handleRemoveArtist = (artistId) => {
    setTaggedArtists(taggedArtists.filter(a => extractObjectId(a._id) !== extractObjectId(artistId)));
  };

  // Artist search handler for Edit modal
  const handleEditArtistSearch = async (searchTerm) => {
    setEditArtistSearch(searchTerm);
    if (searchTerm.length < 2) {
      setEditArtistResults([]);
      setShowEditArtistDropdown(false);
      return;
    }

    try {
      const response = await allAPiServicesCall.getSearchArtist(
        { query: searchTerm },
        getAuthConfig(),
        navigate
      );
      if (response?.data) {
        setEditArtistResults(response.data);
        setShowEditArtistDropdown(true);
      }
    } catch (error) {
      console.error('Error searching artists:', error);
    }
  };

  const handleEditAddArtist = (artist) => {
    if (!editTaggedArtists.find(a => extractObjectId(a._id) === extractObjectId(artist._id))) {
      setEditTaggedArtists([...editTaggedArtists, artist]);
    }
    setEditArtistSearch('');
    setEditArtistResults([]);
    setShowEditArtistDropdown(false);
  };

  const handleEditRemoveArtist = (artistId) => {
    setEditTaggedArtists(editTaggedArtists.filter(a => extractObjectId(a._id) !== extractObjectId(artistId)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const name = formData.get('name').trim();
    const description = formData.get('description').trim();
    const image = formData.get('image');
    const hostName = formData.get('hostName').trim();
    const genre = formData.get('genre').trim();

    if (!name || !description || !hostName || !genre) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const podcastData = {
      name,
      description,
      host: hostName,
      genre: genre.split(',').map(g => g.trim()),
      taggedArtists: taggedArtists.map(artist => extractObjectId(artist._id)),
    };

    if (image && image.size > 0) {
      podcastData.image = image;
    }

    try {
      setLoading(true);
      await allAPiServicesCall.createPodcast(podcastData);
      setShowAddPodcastModal(false);
      setTaggedArtists([]);
      fetchPodcasts(currentPage, searchQuery);
      toast.success('Podcast created successfully!');
    } catch (error) {
      console.error('Error adding podcast:', error);
      toast.error('Failed to add podcast. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPodcast = (podcast) => {
    setShowEditPodcastModel(true);
    setPodcastToEdit(podcast);
    setEditTaggedArtists(podcast.taggedArtists || []);
  };

  const handleDeletePodcast = (id) => {
    setShowDeletePodcastModel(true);
    setPodcastToDelete(id);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await allAPiServicesCall.deletePodcast(podcastToDelete);
      setShowDeletePodcastModel(false);
      fetchPodcasts(currentPage, searchQuery);
      toast.success('Podcast deleted successfully!');
    } catch (error) {
      console.error('Error deleting podcast:', error);
      toast.error('Failed to delete podcast.');
    } finally {
      setLoading(false);
    }
  };

  const handlePodcastEdit = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const name = formData.get('name').trim();
    const description = formData.get('description').trim();
    const image = formData.get('image');
    const hostName = formData.get('hostName').trim();
    const genre = formData.get('genre').trim();

    if (!name || !description || !hostName || !genre) {
      toast.error('Please fill in all required fields.');
      return;
    }

    const podcastData = {
      name,
      description,
      host: hostName,
      genre: genre.split(',').map(g => g.trim()),
      taggedArtists: editTaggedArtists.map(artist => extractObjectId(artist._id)),
    };

    if (image && image.size > 0) {
      podcastData.image = image;
    }

    try {
      setLoading(true);
      await allAPiServicesCall.editPodcast(extractObjectId(podcastToEdit?._id), podcastData);
      setShowEditPodcastModel(false);
      setEditTaggedArtists([]);
      fetchPodcasts(currentPage, searchQuery);
      toast.success('Podcast updated successfully!');
    } catch (error) {
      console.error('Error updating podcast:', error);
      toast.error('Failed to update podcast. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CommonLayout>
      <div className="podcast-page">
        {/* Header Section */}
        <div className="podcast-header">
          <div className="podcast-header-top">
            <div className="podcast-title-wrapper">
              <div className="podcast-title-icon">
                <FaPodcast />
              </div>
              <div className="podcast-title-text">
                <h1 className="podcast-title">Podcast Section</h1>
                <p className="podcast-subtitle">Manage your podcast library and episodes</p>
              </div>
            </div>
            <div className="podcast-stats">
              <div className="podcast-stat-badge">
                <FaMicrophone />
                <span>Total: <strong>{podcasts?.length || 0}</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="podcast-action-bar">
          <div className="podcast-search-wrapper">
            <FaSearch className="podcast-search-icon" />
            <input
              type="text"
              className="podcast-search-input"
              placeholder="Search podcasts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <button
            className="podcast-btn-add"
            onClick={() => setShowAddPodcastModal(true)}
          >
            <FaPlus /> Add Podcast
          </button>
        </div>

        {/* Main Card */}
        <div className="podcast-card">
          {loading ? (
            <div className="podcast-loading">
              <div className="podcast-loading-spinner"></div>
              <span className="podcast-loading-text">Loading podcasts...</span>
            </div>
          ) : podcasts?.length === 0 ? (
            <div className="podcast-empty">
              <div className="podcast-empty-icon">
                <FaPodcast />
              </div>
              <span className="podcast-empty-text">No podcasts found</span>
              <span className="podcast-empty-subtitle">
                Start by adding your first podcast using the button above.
              </span>
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="podcast-table-container">
                <table className="podcast-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>#</th>
                      <th style={{ width: '25%' }}>Title</th>
                      <th style={{ width: '15%' }}>Host</th>
                      <th style={{ width: '35%' }}>Description</th>
                      <th style={{ width: 140 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {podcasts.map((podcast, index) => (
                      <tr key={podcast._id}>
                        <td>
                          <span className="podcast-id">
                            {(currentPage - 1) * 10 + index + 1}
                          </span>
                        </td>
                        <td>
                          <span className="podcast-name">{podcast?.name}</span>
                        </td>
                        <td>
                          <span className="podcast-host-badge">
                            <FaMicrophone size={10} />
                            {podcast?.host || "Unknown"}
                          </span>
                        </td>
                        <td>
                          <span className="podcast-description">
                            {podcast?.description?.length > 100
                              ? podcast.description.substring(0, 100) + "..."
                              : podcast?.description || "—"}
                          </span>
                        </td>
                        <td>
                          <div className="podcast-actions">
                            <button
                              className="podcast-btn podcast-btn-view"
                              title="View Podcast"
                              onClick={() => handlePodcastClick(extractObjectId(podcast._id))}
                            >
                              <FaEye />
                            </button>
                            <button
                              className="podcast-btn podcast-btn-edit"
                              title="Edit Podcast"
                              onClick={() => handleEditPodcast(podcast)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              className="podcast-btn podcast-btn-delete"
                              title="Delete Podcast"
                              onClick={() => handleDeletePodcast(extractObjectId(podcast._id))}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="podcast-mobile-view">
                {podcasts.map((podcast, index) => (
                  <div key={podcast._id} className="podcast-mobile-card">
                    <div className="podcast-mobile-card-header">
                      <div className="podcast-mobile-card-icon">
                        <FaPodcast />
                      </div>
                      <div className="podcast-mobile-card-info">
                        <h3 className="podcast-mobile-card-name">{podcast?.name}</h3>
                        <span className="podcast-mobile-card-host">
                          <FaMicrophone size={10} /> {podcast?.host || "Unknown Host"}
                        </span>
                      </div>
                    </div>
                    <p className="podcast-mobile-card-description">
                      {podcast?.description?.length > 120
                        ? podcast.description.substring(0, 120) + "..."
                        : podcast?.description || "No description available."}
                    </p>
                    <div className="podcast-mobile-card-actions">
                      <button
                        className="podcast-btn podcast-btn-view"
                        onClick={() => handlePodcastClick(extractObjectId(podcast._id))}
                      >
                        <FaEye />
                      </button>
                      <button
                        className="podcast-btn podcast-btn-edit"
                        onClick={() => handleEditPodcast(podcast)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="podcast-btn podcast-btn-delete"
                        onClick={() => handleDeletePodcast(extractObjectId(podcast._id))}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="podcast-pagination">
                  <button
                    className="podcast-pagination-btn"
                    onClick={handlePreviousPage}
                    disabled={currentPage === 1}
                  >
                    <FaChevronLeft /> Previous
                  </button>
                  <div className="podcast-pagination-info">
                    <span className="podcast-pagination-current">{currentPage}</span>
                    <span className="podcast-pagination-separator">/</span>
                    <span className="podcast-pagination-total">{totalPages}</span>
                  </div>
                  <button
                    className="podcast-pagination-btn"
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                  >
                    Next <FaChevronRight />
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Add Podcast Modal */}
        <Modal
          show={showAddPodcastModal}
          onHide={() => { setShowAddPodcastModal(false); setTaggedArtists([]); }}
          centered
          className="podcast-modal"
        >
          <Modal.Header closeButton className="podcast-modal-header">
            <Modal.Title className="podcast-modal-title">
              <FaPlus /> Add New Podcast
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="podcast-modal-body">
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  name="name"
                  type="text"
                  placeholder="Enter podcast name"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  name="description"
                  as="textarea"
                  rows={3}
                  placeholder="Enter podcast description"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Upload Image</Form.Label>
                <Form.Control
                  name="image"
                  type="file"
                  accept="image/*"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Host Name *</Form.Label>
                <Form.Control
                  name="hostName"
                  type="text"
                  placeholder="Enter host name"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Genre (comma-separated) *</Form.Label>
                <Form.Control
                  name="genre"
                  type="text"
                  placeholder="Enter genres (e.g., Music, Pop, Rock)"
                  required
                />
              </Form.Group>

              {/* Artist Tagging Section */}
              <Form.Group className="mb-3">
                <Form.Label>Tag Artists</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    value={artistSearch}
                    onChange={(e) => handleArtistSearch(e.target.value)}
                    placeholder="Search for artists to tag..."
                  />
                  {showArtistDropdown && artistResults.length > 0 && (
                    <div className="podcast-artist-dropdown">
                      {artistResults.map((artist) => (
                        <div
                          key={extractObjectId(artist._id)}
                          className="podcast-artist-dropdown-item"
                          onClick={() => handleAddArtist(artist)}
                        >
                          {artist.artist_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {taggedArtists.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted d-block mb-2">Tagged Artists:</small>
                    <div className="d-flex flex-wrap gap-2">
                      {taggedArtists.map((artist) => (
                        <span key={extractObjectId(artist._id)} className="podcast-artist-tag">
                          {artist.artist_name}
                          <button
                            type="button"
                            className="podcast-artist-tag-remove"
                            onClick={() => handleRemoveArtist(extractObjectId(artist._id))}
                          >
                            <FaTimes />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Form.Group>

              <div className="d-flex gap-2 justify-content-end mt-4">
                <Button
                  className="podcast-modal-btn podcast-modal-btn-secondary"
                  onClick={() => { setShowAddPodcastModal(false); setTaggedArtists([]); }}
                >
                  Cancel
                </Button>
                <Button
                  className="podcast-modal-btn podcast-modal-btn-primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add Podcast"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Edit Podcast Modal */}
        <Modal
          show={showEditPodcastModel}
          onHide={() => { setShowEditPodcastModel(false); setEditTaggedArtists([]); }}
          centered
          className="podcast-modal"
        >
          <Modal.Header closeButton className="podcast-modal-header">
            <Modal.Title className="podcast-modal-title">
              <FaEdit /> Edit Podcast
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="podcast-modal-body">
            <Form onSubmit={handlePodcastEdit}>
              <Form.Group className="mb-3">
                <Form.Label>Name *</Form.Label>
                <Form.Control
                  name="name"
                  type="text"
                  placeholder="Enter podcast name"
                  value={podcastToEdit?.name || ''}
                  onChange={(e) => setPodcastToEdit({ ...podcastToEdit, name: e.target.value })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description *</Form.Label>
                <Form.Control
                  name="description"
                  as="textarea"
                  rows={3}
                  placeholder="Enter podcast description"
                  value={podcastToEdit?.description || ''}
                  onChange={(e) => setPodcastToEdit({ ...podcastToEdit, description: e.target.value })}
                  required
                />
              </Form.Group>

              {podcastToEdit?.image?.url && (
                <div className="mb-3">
                  <Form.Label>Current Image</Form.Label>
                  <div>
                    <img
                      src={podcastToEdit?.image?.url}
                      alt="podcast"
                      className="podcast-edit-image-preview"
                    />
                  </div>
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Upload New Image</Form.Label>
                <Form.Control
                  name="image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setPodcastToEdit({ ...podcastToEdit, image: e.target.files[0] })}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Host Name *</Form.Label>
                <Form.Control
                  name="hostName"
                  type="text"
                  placeholder="Enter host name"
                  value={podcastToEdit?.host || ''}
                  onChange={(e) => setPodcastToEdit({ ...podcastToEdit, host: e.target.value })}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Genre (comma-separated) *</Form.Label>
                <Form.Control
                  name="genre"
                  type="text"
                  placeholder="Enter genres"
                  value={podcastToEdit?.genre || ''}
                  onChange={(e) => setPodcastToEdit({ ...podcastToEdit, genre: e.target.value })}
                  required
                />
              </Form.Group>

              {/* Artist Tagging Section for Edit */}
              <Form.Group className="mb-3">
                <Form.Label>Tag Artists</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="text"
                    value={editArtistSearch}
                    onChange={(e) => handleEditArtistSearch(e.target.value)}
                    placeholder="Search for artists to tag..."
                  />
                  {showEditArtistDropdown && editArtistResults.length > 0 && (
                    <div className="podcast-artist-dropdown">
                      {editArtistResults.map((artist) => (
                        <div
                          key={extractObjectId(artist._id)}
                          className="podcast-artist-dropdown-item"
                          onClick={() => handleEditAddArtist(artist)}
                        >
                          {artist.artist_name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {editTaggedArtists.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted d-block mb-2">Tagged Artists:</small>
                    <div className="d-flex flex-wrap gap-2">
                      {editTaggedArtists.map((artist) => (
                        <span key={extractObjectId(artist._id)} className="podcast-artist-tag">
                          {artist.artist_name}
                          <button
                            type="button"
                            className="podcast-artist-tag-remove"
                            onClick={() => handleEditRemoveArtist(extractObjectId(artist._id))}
                          >
                            <FaTimes />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </Form.Group>

              <div className="d-flex gap-2 justify-content-end mt-4">
                <Button
                  className="podcast-modal-btn podcast-modal-btn-secondary"
                  onClick={() => {
                    setShowEditPodcastModel(false);
                    setPodcastToEdit({});
                    setEditTaggedArtists([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="podcast-modal-btn podcast-modal-btn-primary"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          show={showDeletePodcastModel}
          onHide={() => setShowDeletePodcastModel(false)}
          centered
          className="podcast-modal"
        >
          <Modal.Header closeButton className="podcast-modal-header">
            <Modal.Title className="podcast-modal-title">
              <FaExclamationTriangle /> Confirm Delete
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="podcast-modal-body">
            <div className="podcast-delete-warning">
              <div className="podcast-delete-warning-icon">
                <FaExclamationTriangle />
              </div>
              <div className="podcast-delete-warning-text">
                Are you sure you want to delete this podcast? This action cannot be undone.
                Deleting this podcast will also permanently remove all its episodes and associated data.
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="podcast-modal-footer">
            <Button
              className="podcast-modal-btn podcast-modal-btn-secondary"
              onClick={() => setShowDeletePodcastModel(false)}
            >
              Cancel
            </Button>
            <Button
              className="podcast-modal-btn podcast-modal-btn-danger"
              onClick={handleConfirmDelete}
              disabled={loading}
            >
              {loading ? "Deleting..." : "Delete Podcast"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </CommonLayout>
  );
};

export default PodcastSection;
